/* eslint-disable @typescript-eslint/no-explicit-any */
// src/features/auth/api-layer/clients/BaseAPIClient.ts

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig as API_CONFIG } from '@/config/api.config';
import { TokenStorage } from '../../token-management/storage/TokenStorage';
import { APIError, APIResponse, RequestOptions } from '../../types/api.types';

export abstract class BaseAPIClient {
  protected client: AxiosInstance;
  private tokenStorage: TokenStorage;
  private baseURL: string;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || API_CONFIG.baseURL;
    this.tokenStorage = new TokenStorage();
    this.client = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': API_CONFIG.clientVersion,
      },
      withCredentials: true, // For CSRF cookie handling
    });
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        // Add auth token if available
        const token = await this.tokenStorage.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add tenant context if available
        const tenantId = this.getTenantId();
        if (tenantId && config.headers) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => this.handleResponse(response),
      (error) => this.handleError(error)
    );
  }

  // Protected methods for subclasses
  protected async get<T>(
    url: string,
    options?: RequestOptions
  ): Promise<APIResponse<T>> {
    const response = await this.client.get<T>(url, this.buildConfig(options));
    return this.createAPIResponse(response);
  }

  protected async post<T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<APIResponse<T>> {
    const response = await this.client.post<T>(url, data, this.buildConfig(options));
    return this.createAPIResponse(response);
  }

  protected async put<T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<APIResponse<T>> {
    const response = await this.client.put<T>(url, data, this.buildConfig(options));
    return this.createAPIResponse(response);
  }

  protected async patch<T>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<APIResponse<T>> {
    const response = await this.client.patch<T>(url, data, this.buildConfig(options));
    return this.createAPIResponse(response);
  }

  protected async delete<T>(
    url: string,
    options?: RequestOptions
  ): Promise<APIResponse<T>> {
    const response = await this.client.delete<T>(url, this.buildConfig(options));
    return this.createAPIResponse(response);
  }

  // Request configuration builder
  private buildConfig(options?: RequestOptions): AxiosRequestConfig {
    const config: AxiosRequestConfig = {};

    if (options?.headers) {
      config.headers = { ...config.headers, ...options.headers };
    }

    if (options?.params) {
      config.params = options.params;
    }

    if (options?.timeout) {
      config.timeout = options.timeout;
    }

    if (options?.signal) {
      config.signal = options.signal;
    }

    if (options?.skipAuth) {
      config.headers = { ...config.headers, 'X-Skip-Auth': 'true' };
    }

    return config;
  }

  // Response handling
  private handleResponse(response: AxiosResponse): AxiosResponse {
    // Log successful responses in development
    if (API_CONFIG.isDevelopment) {
      console.debug(`[API] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  }

  // Error handling
  private async handleError(error: AxiosError): Promise<never> {
    const apiError = this.createAPIError(error);

    // Log errors in development
    if (API_CONFIG.isDevelopment) {
      console.error('[API Error]', apiError);
    }

    // Don't retry on client errors (4xx) except 401
    if (apiError.status && apiError.status >= 400 && apiError.status < 500 && apiError.status !== 401) {
      throw apiError;
    }

    // Let interceptors handle 401 for token refresh
    throw apiError;
  }

  // Helper methods
  private createAPIResponse<T>(response: AxiosResponse<T>): APIResponse<T> {
    return {
      data: response.data,
      status: response.status,
      headers: response.headers,
      success: response.status >= 200 && response.status < 300,
    };
  }

  private createAPIError(error: AxiosError): APIError {
    const apiError: APIError = {
      message: error.message,
      code: error.code || 'UNKNOWN_ERROR',
      status: error.response?.status,
      response: error.response?.data,
      isNetworkError: !error.response,
      isTimeout: error.code === 'ECONNABORTED',
      originalError: error,
      // severity: 'high', // or set based on your logic and ErrorSeverity type
      timestamp: new Date().toISOString(),
      isAPIError: true,
      name: 'APIError',
    };

    // Extract error message from response if available
    if (error.response?.data) {
      const data = error.response.data as unknown;
      if (typeof data === 'object' && data !== null) {
        const errorData = data as { message?: string; error?: string; code?: string; errors?: unknown };
        apiError.message = errorData.message || errorData.error || apiError.message;
        apiError.code = errorData.code || apiError.code;
        // apiError.errors = errorData.errors; // Validation errors
      }
    }

    return apiError;
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getTenantId(): string | null {
    // This would typically come from your Redux store or context
    // For now, returning null - implement based on your state management
    return null;
  }

  // Public utility methods
  public setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  public clearAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }

  public getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}